import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Newspaper, Mail, ArrowRight, Sparkles, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { api } from '../App';
import { htmlToText } from '../lib/safeHtml';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export const SubscribeBox = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email address'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/newsletter/subscribe', { email });
      toast.success(res.message);
      setDone(true);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  return (
    <Card className="border-2 border-[hsl(var(--lux-gold)/0.6)] rounded-2xl overflow-hidden shadow-[var(--lux-shadow-2)]" style={{ background: 'hsl(var(--lux-royal))' }} data-testid="newsletter-subscribe-box">
      <CardContent className="p-8 text-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--lux-gold))] flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond' }}>Subscribe to our Newsletter</h3>
        </div>
        <p className="text-sm text-white/80 mb-5">
          Share your email and we'll notify you whenever a new newsletter, announcement or update is published.
        </p>
        {done ? (
          <div className="flex items-center gap-2 text-[hsl(var(--lux-gold))] font-semibold" data-testid="newsletter-subscribed-message">
            <Sparkles className="w-5 h-5" /> You're subscribed! Watch your inbox.
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="bg-white text-[hsl(var(--lux-ink))] flex-1 h-11"
              data-testid="newsletter-email-input"
            />
            <Button type="submit" disabled={loading} className="bg-[hsl(var(--lux-gold))] text-white hover:bg-[hsl(var(--lux-bronze))] h-11 px-6" data-testid="newsletter-subscribe-button">
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default function Newsletters() {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/newsletters')
      .then(d => setNewsletters(d.newsletters || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const stripHtml = htmlToText;

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]">
              <Newspaper className="w-3 h-3 mr-1" /> Community News
            </Badge>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="newsletters-page-title">
              News<span className="text-[hsl(var(--lux-gold))]">letters</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto">
              Announcements, updates and stories from Gujarat Samaj Nottingham.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subscribe */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}><SubscribeBox /></motion.div>
        </div>
      </section>

      {/* Newsletter list */}
      <section className="py-12 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}
            </div>
          ) : newsletters.length === 0 ? (
            <Card className="lux-ornate-card text-center p-12" data-testid="newsletters-empty-state">
              <Newspaper className="w-12 h-12 text-[hsl(var(--lux-gold))] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>No newsletters yet</h3>
              <p className="text-[hsl(var(--lux-ink)/0.7)]">Subscribe above and we will let you know as soon as our next newsletter is published.</p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              {newsletters.map((n, idx) => (
                <motion.div key={n.newsletter_id} {...fadeInUp} transition={{ delay: idx * 0.05 }}>
                  <Link to={`/newsletters/${n.newsletter_id}`} data-testid={`newsletter-card-${n.newsletter_id}`}>
                    <Card className="lux-ornate-card h-full hover:shadow-[var(--lux-shadow-2)] transition-all overflow-hidden group">
                      {n.cover_image_url && (
                        <div className="h-44 overflow-hidden">
                          <img src={n.cover_image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--lux-ink)/0.6)] mb-2">
                          <CalendarDays className="w-3 h-3" />
                          {n.published_at ? new Date(n.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                        </div>
                        <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>{n.title}</h3>
                        <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] line-clamp-3 mb-4">{stripHtml(n.content_html).slice(0, 180)}</p>
                        <span className="inline-flex items-center text-sm font-semibold text-[hsl(var(--lux-gold))] group-hover:gap-2 transition-all">
                          Read more <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
